import { ApiConnector } from '@/integrations/api.connector';

import { leasingsMapper } from '../helpers';
import type {
  TGetLeasingOrdersPaginationParams,
  TLeasingCreateDto,
  TLeasingEditDto,
  TLeasingFile,
  TLeasingFileApi,
  TLeasingOrder,
  TLeasingOrderApi,
  TLeasingOrderDetailed,
  TLeasingOrderDetailedApi
} from '../types';

class LeasingApi extends ApiConnector {
  createLeasingDraft(): Promise<{ uuid: string }> {
    return this.call({ path: 'leasings/create', method: 'post' });
  }

  updateLeasing(uuid: string, dto: TLeasingCreateDto): Promise<void> {
    return this.call({
      path: `leasings/${uuid}`,
      method: 'patch',
      body: dto
    });
  }

  async getAllLeasings(
    params: TGetLeasingOrdersPaginationParams
  ): Promise<{ total: number; data: TLeasingOrder[] }> {
    const response = await this.call<
      unknown,
      { total: number; data: TLeasingOrderApi[] }
    >({ path: 'leasings', params });

    return {
      total: response.total,
      data: response.data.map(leasingsMapper.fromApi)
    };
  }

  async getLeasingById(leasingId: string): Promise<TLeasingOrderDetailed> {
    const response = await this.call<unknown, TLeasingOrderDetailedApi>({
      path: `leasings/${leasingId}`
    });

    return leasingsMapper.fromDetailedApi(response);
  }

  async getLeasingFiles(leasingId: string): Promise<TLeasingFile[]> {
    const response = await this.call<unknown, TLeasingFileApi[]>({
      path: `leasings/${leasingId}/files`
    });

    return response.map(leasingsMapper.parseFileFromApi);
  }

  submitLeasingOfferForReview(offerId: string): Promise<void> {
    return this.call({
      path: `leasings/responses/${offerId}/submitForReview`,
      method: 'post'
    });
  }

  approveLeasingOffer(offerId: string): Promise<void> {
    return this.call({
      path: `leasings/responses/${offerId}/approve`,
      method: 'post'
    });
  }

  declineLeasingOffer(offerId: string): Promise<void> {
    return this.call({
      path: `leasings/responses/${offerId}/decline`,
      method: 'post'
    });
  }

  editLeasingOffer(offerId: string, dto: TLeasingEditDto): Promise<void> {
    return this.call({
      path: `leasings/responses/${offerId}/edit`,
      method: 'post',
      body: dto
    });
  }
}

export default new LeasingApi();
